import { FC, useState } from 'react';

import { useMeetTools } from '../../../features/meet/MeetToolsProvider';
import { useAppDispatch } from '../../../hooks/redux';
import { clearMacClientSelections, IMacCardInfo } from '../../../store/mac.slice';

import { ConfirmDialog } from '../../../shared/ui';

import { MacDeckSelectContent } from './mac-deck-select-content';
import { MacGroupPsychologistResults } from './mac-group-psychologist-results';

const MAC_CONFIRM_CLOSE = {
  name: '«МАК карты»',
  description: 'Клиент перестанет видеть карты.',
};

export interface MacGroupPanelProps {
  onClose: () => void;
  onConfirm?: () => void;
  sendMacCardsOpen?: (data: {
    show: boolean;
    cover: string;
    color: string;
    result: { name: string; value: string[] };
  }) => void;
  /** Закрытие МАК у клиентов по data channel LiveKit (и socket для совместимости) */
  sendMacCardsClose?: () => void;
  /** Identity участников на встрече (кроме психолога) — для экрана «Клиенты делают выбор» и табов */
  expectedParticipantIds: string[];
  /** Имена участников для табов (identity -> displayName) */
  participantDisplayNames?: Record<string, string>;
  /** Панель развёрнута на всю высоту поверх видео */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние */
  onExpandToggle?: () => void;
  /** Вызывается при переключении таба — для трансляции карт на «общий экран» клиентам */
  onActiveTabChange?: (participantLabel: string, cards: IMacCardInfo[]) => void;
}

/**
 * Нижняя панель МАК для психолога в групповой терапии.
 * Фазы: выбор колоды → «Клиенты делают выбор» → табы с результатами по участникам.
 * При закрытии крестиком показывается модалка подтверждения, как у остальных инструментов.
 */
export const MacGroupPanel: FC<MacGroupPanelProps> = ({
  onClose,
  onConfirm,
  sendMacCardsOpen,
  sendMacCardsClose,
  expectedParticipantIds,
  participantDisplayNames,
  isExpanded = false,
  onExpandToggle,
  onActiveTabChange,
}) => {
  const dispatch = useAppDispatch();
  const tools = useMeetTools();
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);

  const handleConfirm = () => {
    /**
     * Снимок участников храним в провайдере встречи, чтобы состояние
     * не сбрасывалось при временной перестройке layout (например, screen share).
     */
    tools.setMacGroupExpectedParticipantIds([...expectedParticipantIds]);
    tools.setMacGroupParticipantDisplayNames({ ...participantDisplayNames });
    dispatch(clearMacClientSelections());
    tools.setMacGroupDeckShown(true);
    onConfirm?.();
  };

  const handleRequestClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(false);
    sendMacCardsClose?.();
    onClose();
  };

  const handleCancelClose = () => {
    setIsConfirmCloseOpen(false);
  };

  const confirmMessage = `Вы действительно хотите закрыть инструмент ${MAC_CONFIRM_CLOSE.name}? ${MAC_CONFIRM_CLOSE.description}`;

  const confirmCloseModal = (
    <ConfirmDialog
      isOpen={isConfirmCloseOpen}
      title="Закрытие инструмента"
      message={confirmMessage}
      cancelText="Оставить открытым"
      confirmText="Закрыть инструмент"
      onCancel={handleCancelClose}
      onConfirm={handleConfirmClose}
    />
  );

  if (tools.macGroupDeckShown) {
    return (
      <>
        <MacGroupPsychologistResults
          expectedParticipantIds={tools.macGroupExpectedParticipantIds}
          participantDisplayNames={tools.macGroupParticipantDisplayNames}
          onClose={handleRequestClose}
          isExpanded={isExpanded}
          onExpandToggle={onExpandToggle}
          onActiveTabChange={onActiveTabChange}
        />
        {confirmCloseModal}
      </>
    );
  }

  return (
    <>
      <div className="h-full w-full bg-white rounded-chip md:rounded-card overflow-hidden">
        <MacDeckSelectContent
          onClose={handleRequestClose}
          onConfirm={handleConfirm}
          sendMacCardsOpen={sendMacCardsOpen}
          variant="panel"
          panelLightTheme
          className="bg-white text-ink"
          isExpanded={isExpanded}
          onExpandToggle={onExpandToggle}
        />
      </div>
      {confirmCloseModal}
    </>
  );
};
